if (typeof ProductRelated !== 'function') {
  class ProductRelated extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onInput);
      this.rows.forEach((row) => this.updateRow(row));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onInput);
    }

    get rows() {
      return Array.from(this.querySelectorAll('[data-js-related-row]'));
    }

    onClick(event) {
      const button = event.target.closest('[data-js-related-increment], [data-js-related-decrement]');
      if (!button || button.disabled) return;

      const row = button.closest('[data-js-related-row]');
      const input = row?.querySelector('[data-js-related-quantity]');
      if (!input) return;

      event.preventDefault();
      const previousQuantity = this.normalizeQuantity(input.value, input);
      const delta = button.hasAttribute('data-js-related-increment') ? 1 : -1;
      const nextQuantity = this.normalizeQuantity(previousQuantity + delta, input);
      this.setQuantity(row, nextQuantity, previousQuantity);
    }

    onInput(event) {
      const variantSelect = event.target.closest('[data-js-related-variant]');
      if (variantSelect) {
        this.setVariant(variantSelect.closest('[data-js-related-row]'), variantSelect);
        return;
      }

      const include = event.target.closest('[data-js-related-include]');
      if (include) {
        const row = include.closest('[data-js-related-row]');
        const quantityInput = row?.querySelector('[data-js-related-quantity]');
        if (!quantityInput || quantityInput.disabled) return;
        const previousQuantity = this.normalizeQuantity(quantityInput.value, quantityInput);
        const nextQuantity = include.checked ? Math.max(1, previousQuantity) : 0;
        this.setQuantity(row, this.normalizeQuantity(nextQuantity, quantityInput), previousQuantity);
        return;
      }

      const input = event.target.closest('[data-js-related-quantity]');
      if (!input) return;
      const row = input.closest('[data-js-related-row]');
      const previousQuantity = Number(input.dataset.previousQuantity || 0);
      this.setQuantity(row, this.normalizeQuantity(input.value, input), previousQuantity);
    }

    normalizeQuantity(value, input) {
      const minimum = Number(input?.min || 0);
      const maximum = Number(input?.max || 99);
      const parsed = Number.parseInt(value, 10);
      const quantity = Number.isFinite(parsed) ? parsed : minimum;
      return Math.min(maximum, Math.max(minimum, quantity));
    }

    setQuantity(row, quantity, previousQuantity) {
      const input = row?.querySelector('[data-js-related-quantity]');
      if (!input) return;

      input.value = quantity;
      input.dataset.previousQuantity = String(quantity);
      this.updateRow(row);

      if (quantity !== previousQuantity) {
        this.dispatchEvent(new CustomEvent('pdp-product-related-change', {
          bubbles: true,
          detail: {
            primaryProductId: this.dataset.primaryProductId || null,
            productId: row.dataset.productId || null,
            variantId: row.querySelector('[data-variant-id]')?.dataset.variantId || null,
            previousQuantity,
            quantity
          }
        }));
      }
    }

    setVariant(row, select) {
      const selectedOption = select?.selectedOptions?.[0];
      const eligible = row?.querySelector('[data-js-related-eligible]');
      const input = row?.querySelector('[data-js-related-quantity]');
      if (!selectedOption || !eligible || !input) return;

      const available = selectedOption.dataset.available === 'true';
      eligible.dataset.variantId = selectedOption.value;
      input.disabled = !available;
      if (!available) input.value = input.min || '0';

      const inventoryMax = Number.parseInt(selectedOption.dataset.inventoryMax, 10);
      if (Number.isFinite(inventoryMax) && inventoryMax > 0) input.max = String(inventoryMax);

      if (selectedOption.dataset.priceCents) {
        eligible.dataset.priceCents = selectedOption.dataset.priceCents;
      }

      const price = row.querySelector('[data-js-related-price]');
      if (price && selectedOption.dataset.price) {
        price.textContent = `+${selectedOption.dataset.price}`;
      }

      this.updateRow(row);
    }

    updateRow(row) {
      const input = row.querySelector('[data-js-related-quantity]');
      if (!input) return;
      const quantity = this.normalizeQuantity(input.value, input);
      input.value = quantity;
      input.dataset.previousQuantity = String(quantity);

      const decrement = row.querySelector('[data-js-related-decrement]');
      const increment = row.querySelector('[data-js-related-increment]');
      const minimum = Number(input.min || 0);
      const maximum = Number(input.max || 99);
      if (decrement) decrement.disabled = input.disabled || quantity <= minimum;
      if (increment) increment.disabled = input.disabled || quantity >= maximum;

      const include = row.querySelector('[data-js-related-include]');
      if (include) {
        include.disabled = input.disabled;
        include.checked = !input.disabled && quantity > 0;
      }

      this.updateTotal();
    }

    updateTotal() {
      const total = this.querySelector('[data-js-related-total]');
      if (!total) return;

      const cents = this.rows.reduce((sum, row) => {
        const eligible = row.querySelector('[data-js-related-eligible]');
        const input = row.querySelector('[data-js-related-quantity]');
        if (!eligible || !input || input.disabled) return sum;
        const price = Number(eligible.dataset.priceCents);
        if (!Number.isFinite(price) || price < 0) return sum;
        return sum + price * this.normalizeQuantity(input.value, input);
      }, 0);

      total.textContent = this.formatMoney(cents);
    }

    formatMoney(cents) {
      const format = window.KROWN?.settings?.shop_money_format || '${{amount}}';
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

      const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
        const fixed = (number / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? units + decimal + parts[1] : units;
      };

      let value;
      switch (placeholder?.[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.');
          break;
        case 'amount':
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return placeholder ? format.replace(placeholder[0], value) : `$${value}`;
    }

    getSelectedItems() {
      return this.rows.reduce((items, row) => {
        const eligible = row.querySelector('[data-js-related-eligible][data-variant-id]');
        const input = row.querySelector('[data-js-related-quantity]');
        if (!eligible || !input) return items;

        const id = Number(eligible.dataset.variantId);
        const quantity = this.normalizeQuantity(input.value, input);
        if (!Number.isSafeInteger(id) || id <= 0 || quantity <= 0) return items;

        items.push({
          id,
          quantity,
          productId: row.dataset.productId || null,
          title: row.dataset.productTitle || ''
        });
        return items;
      }, []);
    }
  }

  if (typeof customElements.get('product-related') === 'undefined') {
    customElements.define('product-related', ProductRelated);
  }
}
